import React from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CloseIcon from "@mui/icons-material/Close";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { acceptsField } from "../templateSchema";
import {
  resolvePath,
  interpolate,
  formatValue,
  computeDerived,
  buildTableRows,
  cellValue,
  sumCells,
} from "../render/valueResolver";
import { getDerived, RECORD_LIST_PATH } from "../fieldCatalog";

const MONO = 'ui-monospace, "SF Mono", Menlo, Consolas, monospace';

// "FFRRGGBB" / "RRGGBB" -> "#RRGGBB"
function argbToCss(argb, fallback) {
  if (!argb) return fallback;
  const hex = argb.length === 8 ? argb.slice(2) : argb;
  return "#" + hex;
}

function cellText(record, path, type) {
  if (!record) return { text: `{${path}}`, muted: true };
  const v = formatValue(resolvePath(record, path), type);
  return { text: v === "" ? "—" : v, muted: v === "" };
}

function SectionBody({ section, record, source, dispatch, previewRecords }) {
  const sec = section;

  if (sec.type === "TITLE") {
    const bg = argbToCss(sec.style && sec.style.fillColor, "#3B5BD9");
    const fg = argbToCss(sec.style && sec.style.fontColor, "#FFFFFF");
    return (
      <Box
        sx={{
          bgcolor: bg,
          color: fg,
          textAlign: sec.style && sec.style.align ? sec.style.align : "center",
          fontWeight: 800,
          fontSize: (sec.style && sec.style.fontSize) || 18,
          borderRadius: 1,
          py: 1.2,
          px: 1.5,
        }}
      >
        {record ? interpolate(sec.text, record) || "Title" : sec.text || "Title"}
      </Box>
    );
  }

  if (sec.type === "TEXT") {
    return (
      <Typography variant="body2" color="text.secondary">
        {record ? interpolate(sec.text, record) : sec.text}
      </Typography>
    );
  }

  if (sec.type === "SPACER") {
    return (
      <Box
        sx={{
          height: 18,
          border: "1px dashed",
          borderColor: "divider",
          borderRadius: 1,
          display: "grid",
          placeItems: "center",
          fontSize: 10,
          color: "text.disabled",
        }}
      >
        blank row
      </Box>
    );
  }

  if (sec.type === "DETAIL_FIELDS") {
    const fields = sec.fields || [];
    if (!fields.length) {
      return <Typography variant="caption" color="text.disabled">Drag data fields here →</Typography>;
    }
    const cols = sec.columnsPerRow === 1 ? "1fr" : "1fr 1fr";
    return (
      <Box sx={{ display: "grid", gridTemplateColumns: cols, columnGap: 3, rowGap: 0.5 }}>
        {fields.map((fl, i) => {
          const { text, muted } = cellText(record, fl.path, fl.type);
          return (
            <Box key={i} sx={{ display: "flex", gap: 1, py: 0.25, borderBottom: "1px dotted", borderColor: "divider" }}>
              <Typography variant="body2" fontWeight={700} sx={{ minWidth: 90 }}>
                {fl.label}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: MONO, color: muted ? "text.disabled" : "text.primary" }}>
                {text}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  }

  if (sec.type === "DATA_TABLE") {
    const columns = sec.columns || [];
    return (
      <Box>
        <Box sx={{ display: "flex", gap: 0.5, mb: 0.5, flexWrap: "wrap" }}>
          <Chip
            size="small"
            variant="outlined"
            label={`rows: ${sec.collectionPath === RECORD_LIST_PATH ? "one per record" : sec.collectionPath || "—"}`}
            sx={{ fontFamily: MONO, fontSize: 10 }}
          />
          {sec.rowFilter && sec.rowFilter.path && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`where ${sec.rowFilter.path} = ${sec.rowFilter.value}`}
            />
          )}
        </Box>

        {!columns.length ? (
          <Box sx={{ border: "1px dashed", borderColor: "divider", borderRadius: 1, p: 1.5, textAlign: "center" }}>
            <Typography variant="caption" color="text.disabled">
              Drag a collection's fields here to add columns
            </Typography>
          </Box>
        ) : (
          <TableWithData sec={sec} record={record} source={source} dispatch={dispatch} previewRecords={previewRecords} />
        )}
      </Box>
    );
  }

  if (sec.type === "FOOTER_TOTALS") {
    const rows = sec.rows || [];
    const derived = getDerived(source);
    if (!rows.length) {
      return <Typography variant="caption" color="text.disabled">Drag numeric fields here →</Typography>;
    }
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25 }}>
        {rows.map((r, i) => {
          let text;
          if (!record) {
            text = r.derived ? `{${r.derived}}` : `{${r.path}}`;
          } else {
            const raw = r.derived ? computeDerived(record, derived[r.derived]) : resolvePath(record, r.path);
            text = formatValue(raw, r.type || "currency");
          }
          const bold = r.style && r.style.bold;
          return (
            <Box
              key={i}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                minWidth: 240,
                py: 0.25,
                borderBottom: "1px dotted",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" fontWeight={bold ? 800 : 600} color="text.secondary">
                {r.label}
              </Typography>
              <Typography variant="body2" fontWeight={bold ? 800 : 500} sx={{ fontFamily: MONO }}>
                {text}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  }

  return null;
}

function TableWithData({ sec, record, dispatch, previewRecords }) {
  const columns = sec.columns || [];
  const isList = sec.collectionPath === RECORD_LIST_PATH;
  const placeholder = isList ? !(previewRecords && previewRecords.length) : !record;
  let items = placeholder ? [] : buildTableRows(record, sec, previewRecords); // { rec, el } pairs
  if (isList) items = items.slice(0, 12);

  return (
    <Box sx={{ overflowX: "auto", overscrollBehaviorX: "contain" }}>
      <Table size="small" sx={{ "& td, & th": { fontSize: 12, py: 0.5 } }}>
        <TableHead>
          <TableRow>
            {columns.map((c, ci) => (
              <TableCell key={ci} align={c.align === "right" ? "right" : "left"} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                {c.header}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch({ type: "REMOVE_COLUMN", id: sec.id, index: ci });
                  }}
                  sx={{ ml: 0.5, p: 0.1 }}
                >
                  <CloseIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {placeholder ? (
            <TableRow>
              {columns.map((c, ci) => (
                <TableCell key={ci} align={c.align === "right" ? "right" : "left"} sx={{ fontFamily: MONO, color: "text.disabled" }}>
                  {c.computed ? `= ${c.computed}` : `{${c.path}}`}
                </TableCell>
              ))}
            </TableRow>
          ) : items.length ? (
            items.map((el, ri) => (
              <TableRow key={ri}>
                {columns.map((c, ci) => {
                  const v = formatValue(cellValue(el, c), c.type);
                  return (
                    <TableCell key={ci} align={c.align === "right" ? "right" : "left"} sx={{ fontFamily: MONO }}>
                      {v === "" ? "—" : v}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ color: "text.disabled" }}>
                no matching rows
              </TableCell>
            </TableRow>
          )}

          {sec.showColumnTotals && !placeholder && items.length > 0 && (
            <TableRow>
              {columns.map((c, ci) => (
                <TableCell
                  key={ci}
                  align={ci === 0 ? "left" : c.align === "right" ? "right" : "left"}
                  sx={{ fontWeight: 800, bgcolor: "action.hover" }}
                >
                  {ci === 0 ? "TOTAL" : c.total ? formatValue(sumCells(items, c), c.type) : ""}
                </TableCell>
              ))}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

function SortableSectionCard({ section, selected, onSelect, dispatch, record, source, activeDrag, previewRecords }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
    data: { kind: "SECTION", sectionId: section.id },
  });

  const canDrop =
    activeDrag && activeDrag.kind === "FIELD" && acceptsField(section.type, activeDrag.field);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const badge = {
    TITLE: "Title",
    DETAIL_FIELDS: "Detail",
    DATA_TABLE: "Data table",
    FOOTER_TOTALS: "Totals",
    TEXT: "Text",
    SPACER: "Spacer",
  }[section.type];

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      variant="outlined"
      onClick={() => onSelect(section.id)}
      sx={{
        position: "relative",
        p: 1.25,
        pl: 3.5,
        mb: 1,
        cursor: "pointer",
        borderWidth: 2,
        borderColor: selected ? "primary.main" : canDrop ? "info.main" : "transparent",
        borderStyle: canDrop ? "dashed" : "solid",
        bgcolor: selected ? "action.selected" : "background.paper",
        "&:hover": { bgcolor: selected ? "action.selected" : "action.hover" },
        "&:hover .sec-actions": { opacity: 1 },
      }}
    >
      <Box
        {...listeners}
        {...attributes}
        onClick={(e) => e.stopPropagation()}
        sx={{ position: "absolute", left: 4, top: 10, color: "text.disabled", cursor: "grab", display: "flex" }}
        title="Drag to reorder"
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>

      <Box
        className="sec-actions"
        sx={{ position: "absolute", right: 4, top: 4, display: "flex", alignItems: "center", gap: 0.5, opacity: 0, transition: "opacity .12s" }}
      >
        <Typography variant="caption" color="text.disabled" sx={{ textTransform: "uppercase", fontSize: 9, fontWeight: 700 }}>
          {badge}
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            dispatch({ type: "REMOVE_SECTION", id: section.id });
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <SectionBody section={section} record={record} source={source} dispatch={dispatch} previewRecords={previewRecords} />
    </Paper>
  );
}

export default function BuilderCanvas({ template, previewRecord, previewRecords, selectedId, onSelect, dispatch, activeDrag }) {
  const sections = template.sections || [];
  // Canvas-level drop zone so palette sections can be dropped anywhere on the
  // sheet — including when it has been cleared to empty.
  const { setNodeRef, isOver } = useDroppable({ id: "canvas-root", data: { kind: "CANVAS" } });
  const paletteOver = isOver && activeDrag && activeDrag.kind === "PALETTE_SECTION";

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          mb: 1,
          borderBottom: "1px dashed",
          borderColor: "divider",
        }}
      >
        <Typography variant="caption" sx={{ fontFamily: MONO, color: "text.secondary" }}>
          Sheet: {template.sheet && template.sheet.sheetName}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {previewRecord ? "live preview" : "no record — showing field paths"}
        </Typography>
      </Box>

      <Box
        ref={setNodeRef}
        sx={{
          minHeight: 180,
          borderRadius: 1,
          p: 0.5,
          outline: paletteOver ? "2px dashed" : "2px solid transparent",
          outlineColor: paletteOver ? "primary.main" : "transparent",
          outlineOffset: 2,
          transition: "outline-color .12s",
        }}
      >
        {!sections.length ? (
          <Box sx={{ textAlign: "center", color: "text.disabled", py: 8 }}>
            <Typography>Empty sheet.</Typography>
            <Typography variant="body2">Drag a section from the left here to begin.</Typography>
          </Box>
        ) : (
          <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            {sections.map((section) => (
              <SortableSectionCard
                key={section.id}
                section={section}
                selected={selectedId === section.id}
                onSelect={onSelect}
                dispatch={dispatch}
                record={previewRecord}
                source={template.source}
                activeDrag={activeDrag}
                previewRecords={previewRecords}
              />
            ))}
          </SortableContext>
        )}
      </Box>
    </Box>
  );
}
